import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, Linkedin, CheckCircle2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen pt-32 pb-24">
      {/* Page Header */}
      <section className="px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-sm text-gray-500 uppercase tracking-widest mb-4 font-semibold">
            Let's Connect
          </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 tracking-tight text-white">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Discuss your project requirements with our senior leadership team
          </p>
        </div>
      </section>

      {/* Confidential Callout */}
      <section className="px-8 mb-16">
        <div className="max-w-5xl mx-auto">
          <Card className="bg-white bg-opacity-5 border-white border-opacity-20">
            <CardContent className="p-12 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Discuss a project confidentially
              </h3>
              <p className="text-gray-300 leading-relaxed max-w-2xl mx-auto">
                All project discussions are treated with complete confidentiality. 
                Connect with us to explore how we can support your next major project.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-8 text-white">Contact Information</h2>
              <div className="space-y-8">
                <div className="flex items-start gap-5 group cursor-pointer">
                  <div className="w-14 h-14 bg-white bg-opacity-5 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-opacity-10 transition-all">
                    <Mail className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2 font-semibold uppercase tracking-wider">Email</div>
                    <div className="text-lg text-white group-hover:text-gray-100 transition-colors">
                      contact@apxprojects.com
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-5 group cursor-pointer">
                  <div className="w-14 h-14 bg-white bg-opacity-5 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-opacity-10 transition-all">
                    <Phone className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2 font-semibold uppercase tracking-wider">Phone</div>
                    <div className="text-lg text-white group-hover:text-gray-100 transition-colors">
                      +61 (0) 123 456 789
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-5 group cursor-pointer">
                  <div className="w-14 h-14 bg-white bg-opacity-5 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-opacity-10 transition-all">
                    <MapPin className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2 font-semibold uppercase tracking-wider">Location</div>
                    <div className="text-lg text-white group-hover:text-gray-100 transition-colors">
                      Sydney, Australia
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-5 group cursor-pointer">
                  <div className="w-14 h-14 bg-white bg-opacity-5 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-opacity-10 transition-all">
                    <Linkedin className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2 font-semibold uppercase tracking-wider">LinkedIn</div>
                    <div className="text-lg text-white group-hover:text-gray-100 transition-colors">
                      Connect on LinkedIn
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="bg-navy-card border-navy-border">
                <CardContent className="p-8">
                  {formSubmitted ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="w-16 h-16 text-white mx-auto mb-4" strokeWidth={1.5} />
                      <h3 className="text-2xl font-bold mb-2 text-white">Message Sent</h3>
                      <p className="text-gray-400">We'll be in touch shortly.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Name *</label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="bg-navy-darker border-navy-border text-white placeholder:text-gray-600"
                          placeholder="Your name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Email *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="bg-navy-darker border-navy-border text-white placeholder:text-gray-600"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Company</label>
                        <Input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="bg-navy-darker border-navy-border text-white placeholder:text-gray-600"
                          placeholder="Company name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Phone</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="bg-navy-darker border-navy-border text-white placeholder:text-gray-600"
                          placeholder="+61 xxx xxx xxx"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Message *</label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows={5}
                          className="bg-navy-darker border-navy-border text-white placeholder:text-gray-600"
                          placeholder="Tell us about your project..."
                        />
                      </div>
                      
                      <Button type="submit" className="btn-primary-custom w-full py-6 text-lg">
                        Send Message
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
